import { Loader2, TriangleAlert } from 'lucide-react';
import { useCallback, useState, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog';
import { useI18n } from '@/lib/i18n';

/**
 * OKNO POTWIERDZENIA — zamiast `confirm()` przeglądarki.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️⚠️ ZGŁOSZONE 02.09.2026: „leci jakimś defaultowym alertem z przeglądarki, trzeba to porządnie
 *  obsłużyć". `confirm()` jest tu zły z czterech konkretnych powodów, nie z estetyki:
 *
 *    • pokazuje adres („vendi.ergoflow.app mówi…"), więc pytanie o SPRZĘT KLIENTA wygląda jak
 *      zaczepka strony internetowej;
 *    • przeglądarka dokłada „Nie pozwalaj pytać ponownie" — jedno kliknięcie i najgroźniejsze
 *      operacje portalu przestają cokolwiek pytać, bez śladu w portalu;
 *    • gubi każde formatowanie: akapity zlewają się w ścianę tekstu, a wyróżnienia nie ma,
 *      więc zdanie „tego się nie cofa" wygląda jak reszta;
 *    • blokuje wątek przeglądarki, więc nie da się nawet pokazać kręciołka po potwierdzeniu.
 *
 *  ⚠️ TREŚĆ PRZYJMUJEMY JAKO TEKST Z `\n\n` — tłumaczenia tych pytań już tak wyglądają
 *  (`moduly.domkniecie.pytanie` ma cztery akapity) i nie ma powodu ich przepisywać. Puste linie
 *  robią akapity, pojedyncze złamania zostają złamaniami.
 *
 *  ⚠️ PRZY TONIE `grozne` FOKUS IDZIE NA „ANULUJ". Enter po otwarciu okna nie może wykonać
 *  operacji, której się nie cofa.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export function OknoPotwierdzenia({
  tytul,
  tresc,
  dodatek,
  etykietaPotwierdz,
  ton = 'zwykly',
  pracuje = false,
  onPotwierdz,
  onAnuluj,
}: {
  tytul: string;
  /** Treść pytania; puste linie dzielą na akapity. */
  tresc: string;
  /** Dodatkowa zawartość pod treścią — np. wypisanie tego, co zniknie. */
  dodatek?: ReactNode;
  etykietaPotwierdz: string;
  /** `grozne` = operacja nieodwracalna: czerwony przycisk i fokus na „Anuluj". */
  ton?: 'zwykly' | 'grozne';
  pracuje?: boolean;
  onPotwierdz: () => void;
  onAnuluj: () => void;
}) {
  const { t } = useI18n();
  const akapity = tresc.split(/\n\s*\n/).filter((a) => a.trim() !== '');

  return (
    <Dialog open onOpenChange={(otwarte) => !otwarte && !pracuje && onAnuluj()}>
      <DialogContent title={tytul}>
        <div className="space-y-2 text-sm">
          {ton === 'grozne' && (
            <p className="flex items-start gap-2 rounded-md border border-[var(--destructive)]/40 bg-[var(--destructive)]/10 p-2 text-[13px]">
              <TriangleAlert className="mt-0.5 size-4 shrink-0 text-[var(--destructive)]" />
              <span>{t('potw.nieodwracalne')}</span>
            </p>
          )}
          {akapity.map((a) => (
            /** ⚠️ `whitespace-pre-line`: pojedyncze złamania w akapicie są w tekstach znaczące. */
            <p key={a} className="whitespace-pre-line leading-snug">
              {a}
            </p>
          ))}
        </div>

        {dodatek !== undefined && <div className="mt-3">{dodatek}</div>}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={pracuje}
            onClick={onAnuluj}
            autoFocus={ton === 'grozne'}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            variant={ton === 'grozne' ? 'destructive' : 'default'}
            disabled={pracuje}
            onClick={onPotwierdz}
          >
            {pracuje && <Loader2 className="animate-spin" />}
            {etykietaPotwierdz}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

type Pytanie = {
  tytul: string;
  tresc: string;
  etykietaPotwierdz: string;
  ton?: 'zwykly' | 'grozne';
};

/**
 * PYTANIE JAKO OBIETNICA — zamiennik `if (!confirm(...)) return;` w miejscu, w którym stał.
 *
 * ⚠️ POWSTAŁO 27.10.2026, gdy zostało czternaście `confirm()` w jedenastu komponentach. Stan
 * „które pytanie otwarte" w każdym z nich osobno rozbiłby każdą obsługę kliknięcia na dwie
 * funkcje; tu zostaje jedna linia `if (!(await zapytaj({...}))) return;`, a komponent
 * renderuje `oknoPytania` w swoim JSX.
 *
 * ⚠️ JEDNO PYTANIE NARAZ — okno jest modalne, więc drugiego nie da się zadać, dopóki stoi pierwsze.
 */
export function usePotwierdzenie(): { zapytaj: (p: Pytanie) => Promise<boolean>; oknoPytania: ReactNode } {
  const [otwarte, setOtwarte] = useState<(Pytanie & { odpowiedz: (tak: boolean) => void }) | null>(null);
  const zapytaj = useCallback((p: Pytanie) => new Promise<boolean>((odpowiedz) => setOtwarte({ ...p, odpowiedz })), []);

  const zamknij = (tak: boolean) => {
    otwarte?.odpowiedz(tak);
    setOtwarte(null);
  };
  const oknoPytania =
    otwarte === null ? null : (
      <OknoPotwierdzenia
        tytul={otwarte.tytul}
        tresc={otwarte.tresc}
        etykietaPotwierdz={otwarte.etykietaPotwierdz}
        ton={otwarte.ton}
        onPotwierdz={() => zamknij(true)}
        onAnuluj={() => zamknij(false)}
      />
    );
  return { zapytaj, oknoPytania };
}
